import { Link } from 'react-router-dom';
import { useMe } from '../lib/auth.js';
import { useDownloads, groupByCategory } from '../lib/downloads.js';
import type { DownloadItem } from '@z9-store/core';

export function DownloadsPage(): JSX.Element {
  const me = useMe();
  const q = useDownloads();
  if (q.isLoading) return <p className="text-sm text-slate-500">Loading...</p>;
  if (q.isError) {
    return (
      <div className="rounded bg-red-50 p-4 text-sm text-red-700" data-testid="downloads-error">
        Could not load downloads: {(q.error as Error).message}
      </div>
    );
  }
  const items = q.data ?? [];
  const groups = groupByCategory(items);
  const signedIn = Boolean(me.data);
  return (
    <div data-testid="downloads" className="space-y-10">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Software downloads</h1>
        <p className="mt-2 text-sm text-slate-600">
          The Z9/Flex Server installer and controller firmware. Sign in to download.
        </p>
      </header>
      {groups.map(({ category, items }) => (
        <section key={category}>
          <h2 className="text-sm font-medium uppercase tracking-wider text-slate-500">{category}</h2>
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {items.map((item) => (
              <DownloadCard key={item.key} item={item} signedIn={signedIn} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function DownloadCard({ item, signedIn }: { item: DownloadItem; signedIn: boolean }): JSX.Element {
  const href = item.gated ? `/api/downloads/${encodeURIComponent(item.key)}/redirect` : item.jfrogUrl;
  const cta = item.gated
    ? signedIn
      ? 'Download'
      : 'Sign in to download'
    : 'Open';
  const enabled = !item.gated || signedIn;
  return (
    <div data-testid={`download-${item.key}`} className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-base font-semibold text-slate-900">{item.title}</h3>
        <span className="text-xs uppercase tracking-wider text-slate-500">v{item.version}</span>
      </div>
      <p className="mt-1 text-sm text-slate-600">{item.description}</p>
      <div className="mt-3">
        {enabled ? (
          <a
            href={href}
            target={item.gated ? '_self' : '_blank'}
            rel="noopener noreferrer"
            data-testid={`download-cta-${item.key}`}
            className="inline-block rounded bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800"
          >
            {cta}
          </a>
        ) : (
          <Link
            to={`/login?next=${encodeURIComponent('/downloads')}`}
            data-testid={`download-signin-${item.key}`}
            className="inline-block rounded border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-900 hover:bg-slate-100"
          >
            {cta}
          </Link>
        )}
      </div>
    </div>
  );
}
